import React from 'react';
import { observer } from 'mobx-react';
import { BlueprintsNavigation } from '../shared/blueprints-navigation';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { FormattedMessage, useIntl } from 'react-intl';
import DefaultButton from '../../global/default-button/default-button';
import { ITheadSort, ListLayout } from '../../global/tables/table-list-layout';
import { TGetReportsSortKeys } from '../../../api/reports.dao';
import TableLoadingRow from '../../global/tables/table-loading-row';
import { ConnectionTemplatesVM } from './connection-templates.viewmodel';
import { ConnectionsTemplatesListItem } from './connection-templates-list-item/connection-templates-list-item';
import BluePrintItemDetailsPopover from '../blueprint-item-details-popover/blueprint-item-details-popover';
import {
    BLUEPRINT_CONNECTION_TABLE_DATE_CREATED_TEXT,
    BLUEPRINT_CONNECTION_TABLE_DATE_DESCRIPTION_TEXT,
    BLUEPRINT_CONNECTION_TABLE_DATE_MODIFIED_TEXT,
    BLUEPRINT_CONNECTION_TABLE_NAME_TEXT,
    NEW_CONNECTION_TEMPLATE_TEXT,
    NO_CONNECTION_TEMPLATES_TEXT,
    VIEW_CONNECTION_TEMPLATE_DETAILS_TEXT
} from './connection-templates.texts';

type Props = {
    vm: ConnectionTemplatesVM;
};

export const ConnectionTemplatesPresentation = observer((props: Props): JSX.Element => {
    const intl = useIntl();

    const { vm } = props;

    const isListEmpty = !vm.isLoading && !vm.connectionTemplates.length;

    const tHeads: ITheadSort<TGetReportsSortKeys>[] = [
        { title: intl.formatMessage(BLUEPRINT_CONNECTION_TABLE_NAME_TEXT) },
        { title: intl.formatMessage(BLUEPRINT_CONNECTION_TABLE_DATE_CREATED_TEXT) },
        { title: intl.formatMessage(BLUEPRINT_CONNECTION_TABLE_DATE_MODIFIED_TEXT) },
        { title: intl.formatMessage(BLUEPRINT_CONNECTION_TABLE_DATE_DESCRIPTION_TEXT) },
        { width: '150px' }
    ];

    return (
        <div className='event-blueprints'>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <BlueprintsNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {hasPermission(Permission.CREATE_CONNECTION_TEMPLATES) &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={<FormattedMessage {...NEW_CONNECTION_TEMPLATE_TEXT} />}
                                onClick={vm.openCreateTemplateForm.bind(vm)}
                            />
                        }
                    </div>
                </div>
            </section>
            <section className='fixed-list-layout'>
                <div>
                    <ListLayout sortOptions={{ theads: tHeads }}>
                        {vm.isLoading && <TableLoadingRow colSpan={tHeads.length} />}
                        {isListEmpty &&
                            <tr>
                                <td className='table-loading' colSpan={tHeads.length}>
                                    <FormattedMessage {...NO_CONNECTION_TEMPLATES_TEXT} />
                                </td>
                            </tr>
                        }
                        {!vm.isLoading && vm.connectionTemplates.map(item => {
                            return <ConnectionsTemplatesListItem
                                isSelected={vm.isTemplateSelected(item.blueprintId)}
                                key={item.blueprintId}
                                item={item}
                                openEditModal={vm.editConnectionTemplate.bind(vm, item.blueprintId)}
                                openTemplateDetails={vm.openConnectionTemplateDetails}
                            />;
                        })}
                    </ListLayout>
                </div>
            </section>

            <BluePrintItemDetailsPopover
                title={intl.formatMessage(VIEW_CONNECTION_TEMPLATE_DETAILS_TEXT)}
                template={vm.selectedTemplate}
                closePopover={vm.closeBluePrintItemDetailsPopover}
            />
        </div>
    );
});
